// =============================================================================
// src/lib/eligibilityCore.js - pure helpers for the Eligibility page
// (Eligibility Pulse v2, migration 20261009100000). No Supabase imports so
// the contract test can load it under plain node.
// =============================================================================

// Upcoming visit states from eligibility_upcoming(), most urgent first.
export const UPCOMING_STATES = {
  problem:    { label: "Coverage problem", variant: "red",     rank: 0, attention: true },
  error:      { label: "Check failed",     variant: "amber",   rank: 1, attention: true },
  missing_id: { label: "No member ID",     variant: "amber",   rank: 2, attention: true },
  unchecked:  { label: "Not checked",      variant: "neutral", rank: 3, attention: true },
  pending:    { label: "Waiting",          variant: "blue",    rank: 4, attention: false },
  reviewed:   { label: "Reviewed",         variant: "neutral", rank: 5, attention: false },
  confirmed:  { label: "Covered",          variant: "green",   rank: 6, attention: false },
};
export const stateMeta = (s) => UPCOMING_STATES[s] || { label: s || "Unknown", variant: "neutral", rank: 9, attention: false };

// Check statuses (eligibility_checks.status).
export const CHECK_STATUS = {
  "Verified":     { label: "Verified",     variant: "green" },
  "Pending":      { label: "Pending",      variant: "blue" },
  "Not Verified": { label: "Not verified", variant: "red" },
  "Inactive":     { label: "Inactive",     variant: "red" },
  "Error":        { label: "Check failed", variant: "amber" },
};
export const checkStatusMeta = (s) => CHECK_STATUS[s] || { label: s || "Unknown", variant: "neutral" };

// Review outcomes a person can pick (eligibility_review). "retried" and
// "rechecked" are set by the system and only displayed.
export const OUTCOMES = [
  { value: "coverage_confirmed", label: "Coverage confirmed", hint: "Verified by phone, portal, or a new card" },
  { value: "insurance_updated",  label: "Insurance updated",  hint: "Fixed the member ID, DOB, or plan on file" },
  { value: "renewal_referral",   label: "Sent to renew",      hint: "Referred to DSS / ePASS to renew Medicaid" },
  { value: "self_pay",           label: "Self-pay",           hint: "Patient will pay for this visit" },
  { value: "rescheduled",        label: "Rescheduled",        hint: "Visit moved until coverage is sorted out" },
  { value: "payer_called",       label: "Called the plan",    hint: "Spoke with the health plan or NCTracks" },
  { value: "patient_contacted",  label: "Patient contacted",  hint: "Left a message or spoke with the patient" },
  { value: "no_action",          label: "No action needed",   hint: "Not a real problem" },
  { value: "other",              label: "Other",              hint: "Add a note" },
];
const OUTCOME_LABEL = Object.fromEntries(OUTCOMES.map((o) => [o.value, o.label]));
OUTCOME_LABEL.retried = "Retried automatically";
OUTCOME_LABEL.rechecked = "Checked again";
export const outcomeLabel = (v) => OUTCOME_LABEL[v] || v || "";

// Coverage changes from eligibility_changes().
export const CHANGE_KINDS = {
  lost_coverage:    { label: "Coverage ended",           variant: "red",   icon: "!" },
  pcp_elsewhere:    { label: "PCP is another practice",   variant: "red",   icon: "!" },
  plan_changed:     { label: "Plan changed",              variant: "amber", icon: "~" },
  plan_on_file:     { label: "Plan on file differs",      variant: "amber", icon: "~" },
  coverage_ending:  { label: "Coverage ending",           variant: "amber", icon: "~" },
  tcm_new:          { label: "Tailored Care Management",  variant: "blue",  icon: "+" },
  medicare_missing: { label: "Medicare not on file",      variant: "amber", icon: "~" },
};
export const changeMeta = (k) => CHANGE_KINDS[k] || { label: k || "Change", variant: "neutral", icon: "~" };

// "2026-10-14" -> local Date (no timezone shift).
export function ymd(s) {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}
export function localYmd(d = new Date()) {
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}

// "Today" / "Tomorrow" / "Mon, Oct 6".
export function dayHeading(dateStr, now = new Date()) {
  const d = ymd(dateStr);
  if (!d) return "";
  const today = ymd(localYmd(now));
  const diff = Math.round((d - today) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

// Visit time from start_at, else from a 15-minute start_slot.
export function visitTime(row) {
  if (row && row.start_at) {
    const d = new Date(row.start_at);
    if (!isNaN(d.getTime())) return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  if (row && Number.isFinite(row.start_slot)) {
    const mins = row.start_slot * 15;
    const h = Math.floor(mins / 60), m = mins % 60;
    const h12 = ((h + 11) % 12) + 1;
    return h12 + ":" + String(m).padStart(2, "0") + (h < 12 ? " AM" : " PM");
  }
  return "";
}

// Upcoming rows grouped by visit day, each day sorted by urgency then time.
// hideConfirmed drops covered visits (counted per day as hidden).
export function groupUpcoming(rows, { hideConfirmed = true } = {}) {
  const days = new Map();
  for (const r of rows || []) {
    const key = r.appt_date;
    if (!days.has(key)) days.set(key, { date: key, rows: [], hidden: 0, attention: 0, total: 0 });
    const g = days.get(key);
    g.total++;
    if (stateMeta(r.state).attention) g.attention++;
    if (hideConfirmed && (r.state === "confirmed" || r.state === "reviewed")) { g.hidden++; continue; }
    g.rows.push(r);
  }
  const out = [...days.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  for (const g of out) {
    g.rows.sort((a, b) => stateMeta(a.state).rank - stateMeta(b.state).rank
      || String(a.start_at || "").localeCompare(String(b.start_at || ""))
      || (a.start_slot || 0) - (b.start_slot || 0));
  }
  return out;
}

export function pct(part, total) {
  if (!total) return null;
  return Math.round((100 * part) / total);
}

// Relative age: "12m", "5h", "3d".
export function age(iso, now = Date.now()) {
  if (!iso) return "";
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const m = Math.round(ms / 60000);
  if (m < 60) return m + "m";
  const h = Math.round(m / 60);
  if (h < 48) return h + "h";
  return Math.round(h / 24) + "d";
}

// Honest retry line for a failed check.
//   retryEnabled false -> no promise of automatic retries.
export function retryText(row, { retryEnabled = false, outage = false } = {}) {
  if (!row || (row.status !== "Error" && row.check_status !== "Error")) return null;
  const count = row.retry_count || 0;
  const next = row.next_retry_at ? new Date(row.next_retry_at) : null;
  if (next && retryEnabled) {
    const t = next.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    const sameDay = localYmd(next) === localYmd(new Date());
    const when = sameDay ? t : next.toLocaleDateString("en-US", { weekday: "short" }) + " " + t;
    return (outage ? "NCTracks is down; " : "") + "Retrying automatically at " + when + " (attempt " + (count + 2) + " of 4).";
  }
  if (count >= 3) return "Tried " + (count + 1) + " times. Check again later or verify in the NCTracks portal.";
  if (row.retryable === false) return "This failure will not fix itself. Review the details and correct the record.";
  return "Not retried automatically. Use Check again.";
}

// Recommended next steps by status (shown in the review panel). The Error
// steps no longer claim a retry is queued unless one is.
export function recommendedActions(status, { retryScheduled = false } = {}) {
  switch (status) {
    case "Inactive":
      return [
        "Contact the patient before the visit: coverage shows inactive.",
        "If they missed a renewal, refer them to DSS or ePASS to renew.",
        "Plan the visit as self-pay or reschedule until coverage is restored.",
      ];
    case "Not Verified":
      return [
        "Confirm the Medicaid ID, legal name and date of birth with the patient.",
        "Fix the insurance record, then Check again.",
        "If it still fails, verify in the NCTracks portal or call the plan.",
      ];
    case "Pending":
      return [
        "NCTracks has not answered yet; check back in a few hours.",
        "If the visit is today, verify in the NCTracks portal.",
        "Hold the claim until coverage is confirmed.",
      ];
    case "Error":
      return retryScheduled
        ? ["An automatic retry is scheduled; nothing to do unless it is urgent.", "For a visit today, verify in the NCTracks portal."]
        : ["Use Check again. If NCTracks is down, try again later.", "For a visit today, verify in the NCTracks portal."];
    default:
      return ["Collect any copay at check-in.", "No further action needed."];
  }
}

export function eligibilityError(e) {
  const m = String((e && e.message) || e || "");
  if (m.includes("not_authorized")) return "You don't have permission to do that.";
  if (m.includes("note_required")) return "Add a note when you choose Other.";
  if (m.includes("note_length")) return "Keep the note under 1,000 characters.";
  if (m.includes("bad_outcome")) return "Pick what you did.";
  if (m.includes("not_found")) return "That check is no longer available. Refresh the page.";
  return m || "Something went wrong.";
}

// Technical failure text -> what staff should understand.
export function plainMessage(msg) {
  const m = String(msg || "");
  if (!m) return m;
  if (/^No 271 payload|^Transmit failed/i.test(m)) return "NCTracks didn't answer this check.";
  if (/^AAA reject: .*\b(42|80)\b/.test(m)) return "NCTracks was temporarily unavailable.";
  if (/^AAA reject: .*\b(71|72|73|75|76)\b/.test(m)) return "Member not found. Verify the Medicaid ID, name, and date of birth, then try again.";
  if (/^AAA reject/.test(m)) return "NCTracks rejected the check (" + m.replace(/^AAA reject:\s*/, "code ") + ").";
  if (/^Subscriber not located/i.test(m)) return "NCTracks did not return this member.";
  return m;
}
